import isNil from 'lodash/isNil';

import { EM_DASH } from 'constants/index';
import Sample from 'types/models/sample';
import { ErrorCode, NON_PROBLEMATIC_SAMPLE_STATUS_CODES, SampleStatusCode } from 'types/sample.enum';

type SampleLike = Pick<Sample, 'errorCode' | 'statusCode' | 'multiDimValues'>;

type SampleWithRootStatusCode = { statusCode: SampleStatusCode };

function assertIsSampleWithRootStatusCode(sample: SampleLike): asserts sample is SampleWithRootStatusCode {
  if (isNil(sample.statusCode)) {
    throw new Error(`Expected a sample with root statusCode but received ${JSON.stringify(sample)}`);
  }
}
/**
 * Checks if a sample has a specific error code in top-level or multiDimValues.
 * For NO_ERROR checks, treats nil/undefined as NO_ERROR.
 */
function sampleHasErrorCode(sample: SampleLike, errorCode: ErrorCode): boolean {
  const isCheckingForNoError = errorCode === ErrorCode.NO_ERROR;

  // Helper: Check if a single error code matches what we're looking for
  const hasNoError = (code: ErrorCode | null | undefined): boolean => {
    return isNil(code) || code === ErrorCode.NO_ERROR;
  };

  const hasSpecificError = (code: ErrorCode | null | undefined): boolean => {
    return code === errorCode;
  };

  // When checking for NO_ERROR: sample must have no error AND all dims must have no error
  if (isCheckingForNoError) {
    const topLevelHasNoError = hasNoError(sample.errorCode);
    if (!topLevelHasNoError) {
      return false;
    }

    const dims = sample.multiDimValues?.sampleDim;
    if (dims && dims.length > 0) {
      const allDimsHaveNoError = dims.every(dim => hasNoError(dim.errorCode));
      return allDimsHaveNoError;
    }

    return true;
  }

  // When checking for a specific error: sample OR any dim has the error
  const topLevelHasError = hasSpecificError(sample.errorCode);
  if (topLevelHasError) {
    return true;
  }

  const dims = sample.multiDimValues?.sampleDim;
  if (dims && dims.length > 0) {
    const anyDimHasError = dims.some(dim => hasSpecificError(dim.errorCode));
    return anyDimHasError;
  }

  return false;
}

// BE already propagates statusCode from the sampleDim to the root sample, so FE does not need to check for each dim.
function sampleHasStatusCode(sample: SampleWithRootStatusCode, statusCode: SampleStatusCode): boolean {
  return sample.statusCode === statusCode;
}

/**
 * Checks if a sample has any out-of-range reading in top-level or multiDimValues.
 */
function isSampleOutOfRange(sample: SampleLike): boolean {
  // TODO: still wait for a decision on which StatusCode will represent Out Of Range.
  return sampleHasErrorCode(sample, ErrorCode.OUT_OF_RANGE);
}

/**
 * Checks if a sample reflects a missed transmission in top-level or multiDimValues.
 */
function isTransmissionMissed(sample: SampleLike): boolean {
  try {
    assertIsSampleWithRootStatusCode(sample);
    return sampleHasStatusCode(sample, SampleStatusCode.NO_TX_RECEIVED);
  } catch (error) {
    return sampleHasErrorCode(sample, ErrorCode.MISSING_DATA);
  }
}

function isBadSensor(sample: SampleLike): boolean {
  try {
    assertIsSampleWithRootStatusCode(sample);
    return sampleHasStatusCode(sample, SampleStatusCode.BAD_SENSOR)
      || sampleHasStatusCode(sample, SampleStatusCode.BAD_SENSOR_H)
      || sampleHasStatusCode(sample, SampleStatusCode.BAD_SENSOR_L);
  } catch (error) {
    return false;
  }
}

/**
 * Checks if a sample is error-free.
 */
export function isSampleErrorFree(sample: SampleLike): boolean {
  try {
    assertIsSampleWithRootStatusCode(sample);
    return NON_PROBLEMATIC_SAMPLE_STATUS_CODES.includes(sample.statusCode);
  } catch (error) {
    return sampleHasErrorCode(sample, ErrorCode.NO_ERROR);
  }
}

export function isSampleProblematic(sample: SampleLike): boolean {
  return isSampleOutOfRange(sample)
    || isTransmissionMissed(sample)
    || isBadSensor(sample);
}

function shouldDashOutSampleReading(sample: SampleLike): boolean {
  return isTransmissionMissed(sample) || isBadSensor(sample);
}

/**
 * Formats a sample reading based on its status code.
 * If the sample should be dashed out (e.g., NO_TX_RECEIVED, BAD_SENSOR), returns EM_DASH.
 * Otherwise, returns the formatted value as-is.
 */
export function formatSampleReadingByStatus<T>(
  sample: SampleLike,
  formattedValue: T
): T | string {
  if (shouldDashOutSampleReading(sample)) return EM_DASH;
  return formattedValue;
}

function shouldDashOutSensorReading(latestSampleStatus: SampleStatusCode): boolean {
  const isProblematicInLatestSample = NON_PROBLEMATIC_SAMPLE_STATUS_CODES.includes(latestSampleStatus) === false;
  return isProblematicInLatestSample;
}

export function formatSensorReadingByLatestSampleStatus<T>(
  latestSampleStatus: SampleStatusCode,
  formattedValue: T
): T | string {
  if (shouldDashOutSensorReading(latestSampleStatus)) return EM_DASH;
  return formattedValue;
}